import React, { useState, useEffect } from "react";
import api from '../../api';

const PvPChallengeModal = ({ onClose, targetUsername, onChallengeSuccess }) => {
  const [activeTab, setActiveTab] = useState("battle_type");
  const [battleType, setBattleType] = useState("casual");
  const [betAmount, setBetAmount] = useState(0);
  const [currencyType, setCurrencyType] = useState("coins");
  const [itemsAllowed, setItemsAllowed] = useState(true);
  const [maxItemsPerPlayer, setMaxItemsPerPlayer] = useState(0); // 0 = unlimited
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [userBalance, setUserBalance] = useState({ coins: 0, battle_coins: 0 });
  const [dailyLimits, setDailyLimits] = useState({ coins_bet_today: 0, battle_coins_bet_today: 0 });

  // Get theme colors from localStorage
  const getThemeColors = () => {
    const theme = localStorage.getItem("appTheme") || "default";
    
    const themeColors = {
      default: { primary: "#556ee6", secondary: "#2a3042" },
      ocean: { primary: "#17a2b8", secondary: "#1a2332" },
      forest: { primary: "#28a745", secondary: "#1e2d1e" },
      sunset: { primary: "#fd7e14", secondary: "#2d1e1e" },
      purple: { primary: "#6f42c1", secondary: "#2d1e2d" },
      crimson: { primary: "#dc3545", secondary: "#2d1e1e" }
    };
    
    return themeColors[theme] || themeColors.default;
  };

  const themeColors = getThemeColors();

  useEffect(() => {
    fetchUserBalance();
    fetchDailyLimits();
  }, []);

  const fetchUserBalance = async () => {
    try {
      const response = await api.get("/v1/user/balance", { withCredentials: true });
      setUserBalance(response.data);
    } catch (error) {
      console.error("Error fetching user balance:", error);
    }
  };

  const fetchDailyLimits = async () => {
    try {
      const response = await api.get("/v1/pvp/daily-limits", { withCredentials: true });
      setDailyLimits(response.data);
    } catch (error) {
      console.error("Error fetching daily limits:", error);
    }
  };

  const handleBattleTypeChange = (type) => {
    setBattleType(type);
    if (type === "casual") {
      setBetAmount(0);
      setActiveTab("battle_type"); // Switch back to battle type tab if casual is selected
    } else {
      // Set currency type based on battle type
      if (type === "coin_bet") {
        setCurrencyType("coins");
      } else if (type === "battle_coin_bet") {
        setCurrencyType("battle_coins");
      }
    }
  };

  const handleBetAmountChange = (amount) => {
    const numAmount = parseInt(amount) || 0;
    
    // Check daily limits
    const maxDaily = currencyType === "coins" ? 50000 : 75000;
    const usedToday = currencyType === "coins" ? dailyLimits.coins_bet_today : dailyLimits.battle_coins_bet_today;
    const remainingDaily = maxDaily - usedToday;
    
    // Check user balance
    const balance = currencyType === "coins" ? userBalance.coins : userBalance.battle_coins;
    
    const maxAllowed = Math.min(remainingDaily, balance);
    
    if (numAmount > maxAllowed) {
      setBetAmount(maxAllowed);
    } else {
      setBetAmount(numAmount);
    }
  };

  const handleSubmit = async () => {
    if (loading) return;

    // Validation
    if (battleType !== "casual" && betAmount <= 0) {
      setErrorMessage("Bet amount must be greater than 0 for betting battles");
      return;
    }

    if (battleType !== "casual") {
      const balance = currencyType === "coins" ? userBalance.coins : userBalance.battle_coins;
      if (betAmount > balance) {
        setErrorMessage(`Insufficient ${currencyType.replace('_', ' ')}. You have ${balance.toLocaleString()}`);
        return;
      }

      const maxDaily = currencyType === "coins" ? 50000 : 75000;
      const usedToday = currencyType === "coins" ? dailyLimits.coins_bet_today : dailyLimits.battle_coins_bet_today;
      if (usedToday + betAmount > maxDaily) {
        setErrorMessage(`Daily betting limit exceeded. You can bet ${(maxDaily - usedToday).toLocaleString()} more ${currencyType.replace('_', ' ')} today`);
        return;
      }
    }

    setLoading(true);
    setErrorMessage(null);

    try {
      const response = await api.post("/v1/pvp/challenge", {
        opponent_username: targetUsername,
        battle_type: battleType,
        bet_amount: betAmount,
        currency_type: currencyType,
        items_allowed: itemsAllowed,
        max_items_per_player: maxItemsPerPlayer
      }, { withCredentials: true });

      if (response.data.success) {
        setSuccessMessage(`Challenge sent to ${targetUsername}!`);
        setTimeout(() => {
          onChallengeSuccess?.();
          onClose();
        }, 1500);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || "Challenge failed!";
      setErrorMessage(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setLoading(false);
    }
  };

  const getMaxBetAmount = () => {
    const maxDaily = currencyType === "coins" ? 50000 : 75000;
    const usedToday = currencyType === "coins" ? dailyLimits.coins_bet_today : dailyLimits.battle_coins_bet_today;
    const remainingDaily = maxDaily - usedToday;
    const balance = currencyType === "coins" ? userBalance.coins : userBalance.battle_coins;
    return Math.min(remainingDaily, balance);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px 10px',
      boxSizing: 'border-box',
      zIndex: 1050
    }} onClick={onClose}>
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '700px',
          maxHeight: 'calc(100vh - 40px)',
          background: 'rgba(29, 34, 46, 0.95)',
          color: '#fff',
          borderRadius: '12px',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${themeColors.primary}40`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: `1px solid ${themeColors.primary}40`,
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img
              src="/items/battleseek.png"
              alt="Battle Challenge"
              width={32}
              height={32}
              style={{ imageRendering: 'pixelated' }}
            />
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: themeColors.primary }}>
              Challenge {targetUsername}
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'transparent',
              border: 'none',
              color: themeColors.primary,
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px'
            }}
            onMouseEnter={(e) => e.target.style.background = `${themeColors.primary}20`}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          borderBottom: `1px solid ${themeColors.primary}40`,
          background: 'rgba(0, 0, 0, 0.1)'
        }}>
          <button
            onClick={() => setActiveTab("battle_type")}
            style={{
              padding: '12px 16px',
              background: activeTab === 'battle_type' ? `${themeColors.primary}40` : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'battle_type' ? `2px solid ${themeColors.primary}` : 'none',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: activeTab === 'battle_type' ? 'bold' : 'normal',
              flex: '1 1 auto',
              minWidth: '100px'
            }}
          >
            Battle Type
          </button>
          <button
            onClick={() => battleType !== 'casual' && setActiveTab("betting")}
            disabled={battleType === 'casual'}
            style={{
              padding: '12px 16px',
              background: activeTab === 'betting' ? `${themeColors.primary}40` : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'betting' ? `2px solid ${themeColors.primary}` : 'none',
              color: battleType === 'casual' ? 'rgba(255, 255, 255, 0.4)' : '#fff',
              cursor: battleType === 'casual' ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: activeTab === 'betting' ? 'bold' : 'normal',
              flex: '1 1 auto',
              minWidth: '100px',
              opacity: battleType === 'casual' ? 0.5 : 1
            }}
          >
            Betting {battleType === 'casual' && '(Disabled)'}
          </button>
          <button
            onClick={() => setActiveTab("items")}
            style={{
              padding: '12px 16px',
              background: activeTab === 'items' ? `${themeColors.primary}40` : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'items' ? `2px solid ${themeColors.primary}` : 'none',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: activeTab === 'items' ? 'bold' : 'normal',
              flex: '1 1 auto',
              minWidth: '100px'
            }}
          >
            Item Rules
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '20px',
          overflowY: 'auto',
          flex: 1,
          background: 'rgba(0, 0, 0, 0.05)'
        }}>
          {errorMessage && (
            <div style={{
              background: 'rgba(220, 38, 38, 0.1)',
              border: '1px solid rgba(220, 38, 38, 0.3)',
              color: '#fca5a5',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {errorMessage}
            </div>
          )}

          {successMessage && (
            <div style={{
              background: `rgba(34, 197, 94, 0.1)`,
              border: '1px solid rgba(34, 197, 94, 0.3)',
              color: '#86efac',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {successMessage}
            </div>
          )}

          {/* Tab Content */}
          {activeTab === "battle_type" && (
            <div>
              <h4 style={{ color: themeColors.primary, marginBottom: '16px', fontSize: '16px' }}>
                Choose Battle Type
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { value: 'casual', label: 'Casual Battle', desc: 'No betting, just for fun', icon: '/items/pokeball.png' },
                  { value: 'coin_bet', label: 'Coin Battle', desc: 'Bet regular coins', icon: '/items/coin.png' },
                  { value: 'battle_coin_bet', label: 'Battle Coin Battle', desc: 'Bet battle coins', icon: '/items/battle_coin.png' }
                ].map(type => (
                  <div key={type.value} 
                    onClick={() => handleBattleTypeChange(type.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '16px',
                      border: `2px solid ${battleType === type.value ? themeColors.primary : 'rgba(255, 255, 255, 0.1)'}`,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      background: battleType === type.value ? `${themeColors.primary}20` : 'rgba(255, 255, 255, 0.05)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (battleType !== type.value) {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (battleType !== type.value) {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                      }
                    }}
                  >
                    <img 
                      src={type.icon} 
                      alt={type.label}
                      style={{ width: '32px', height: '32px', marginRight: '16px', imageRendering: 'pixelated' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', color: '#fff', fontSize: '15px' }}>{type.label}</div>
                      <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', marginTop: '4px' }}>{type.desc}</div>
                    </div>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: `2px solid ${battleType === type.value ? themeColors.primary : 'rgba(255, 255, 255, 0.3)'}`,
                      background: battleType === type.value ? themeColors.primary : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {battleType === type.value && (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: '#fff'
                        }} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "betting" && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <h4 style={{ color: themeColors.primary, margin: 0, fontSize: '16px' }}>
                  Betting Configuration
                </h4>
                <div style={{
                  padding: '4px 12px',
                  background: `${themeColors.primary}20`,
                  border: `1px solid ${themeColors.primary}40`,
                  borderRadius: '16px',
                  fontSize: '12px',
                  color: themeColors.primary,
                  fontWeight: 'bold'
                }}>
                  {battleType === 'coin_bet' ? 'COIN BATTLE' : 'BATTLE COIN BATTLE'}
                </div>
              </div>
              {battleType === 'casual' ? (
                <div style={{
                  padding: '20px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  textAlign: 'center',
                  color: 'rgba(255, 255, 255, 0.7)'
                }}>
                  <img src="/items/pokeball.png" alt="Casual" style={{ width: '48px', height: '48px', marginBottom: '12px', imageRendering: 'pixelated' }} />
                  <p>Casual battles don't involve any betting.<br />Switch to Coin Battle or Battle Coin Battle to configure betting.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#fff' }}>
                      Currency Type
                    </label>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      {[
                        { value: 'coins', label: 'Regular Coins', icon: '/items/coin.png' },
                        { value: 'battle_coins', label: 'Battle Coins', icon: '/items/battle_coin.png' }
                      ].map(currency => {
                        // Disable currency based on battle type
                        const isDisabled = (battleType === 'coin_bet' && currency.value === 'battle_coins') || 
                                         (battleType === 'battle_coin_bet' && currency.value === 'coins');
                        
                        return (
                          <div key={currency.value}
                            onClick={() => !isDisabled && setCurrencyType(currency.value)}
                            style={{
                              flex: 1,
                              padding: '12px',
                              border: `2px solid ${currencyType === currency.value ? themeColors.primary : 'rgba(255, 255, 255, 0.1)'}`,
                              borderRadius: '8px',
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                              background: currencyType === currency.value ? `${themeColors.primary}20` : 'rgba(255, 255, 255, 0.05)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              opacity: isDisabled ? 0.4 : 1
                            }}
                          >
                            <img src={currency.icon} alt={currency.label} style={{ width: '20px', height: '20px', imageRendering: 'pixelated' }} />
                            <span style={{ fontSize: '14px', color: isDisabled ? 'rgba(255, 255, 255, 0.4)' : '#fff' }}>
                              {currency.label}
                              {isDisabled && ' (N/A)'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#fff' }}>
                      Bet Amount
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="number"
                        min="1"
                        max={getMaxBetAmount()}
                        value={betAmount}
                        onChange={(e) => handleBetAmountChange(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px 50px 12px 16px',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          color: '#fff',
                          boxSizing: 'border-box'
                        }}
                        placeholder="Enter bet amount"
                      />
                      <img 
                        src={`/items/${currencyType === 'coins' ? 'coin' : 'battle_coin'}.png`}
                        alt={currencyType}
                        style={{
                          position: 'absolute',
                          right: '16px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '20px',
                          height: '20px',
                          imageRendering: 'pixelated'
                        }}
                      />
                    </div>
                    <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Your Balance:</span>
                        <span style={{ fontWeight: 'bold', color: '#fff' }}>
                          {(currencyType === 'coins' ? userBalance.coins : userBalance.battle_coins).toLocaleString()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Already Bet Today:</span>
                        <span style={{ fontWeight: 'bold', color: '#fbbf24' }}>
                          {(currencyType === 'coins' ? dailyLimits.coins_bet_today : dailyLimits.battle_coins_bet_today).toLocaleString()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Can Bet Today:</span>
                        <span style={{ fontWeight: 'bold', color: '#10b981' }}>
                          {getMaxBetAmount().toLocaleString()}
                        </span>
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.5)', marginTop: '4px', fontStyle: 'italic' }}>
                        Daily limit: {(currencyType === 'coins' ? '50,000' : '75,000')} {currencyType.replace('_', ' ')}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "items" && (
            <div>
              <h4 style={{ color: themeColors.primary, marginBottom: '16px', fontSize: '16px' }}>
                Item Usage Rules
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div 
                  onClick={() => setItemsAllowed(!itemsAllowed)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px',
                    border: `2px solid ${itemsAllowed ? themeColors.primary : 'rgba(255, 255, 255, 0.1)'}`,
                    borderRadius: '12px',
                    cursor: 'pointer',
                    background: itemsAllowed ? `${themeColors.primary}20` : 'rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <img src="/items/potion.png" alt="Items" style={{ width: '32px', height: '32px', marginRight: '16px', imageRendering: 'pixelated' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold', color: '#fff' }}>Allow Items in Battle</div>
                    <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', marginTop: '4px' }}>
                      {itemsAllowed ? 'Players can use healing items and revives' : 'No items allowed - pure battle skills only'}
                    </div>
                  </div>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    border: `2px solid ${itemsAllowed ? themeColors.primary : 'rgba(255, 255, 255, 0.3)'}`,
                    background: itemsAllowed ? themeColors.primary : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {itemsAllowed && <span style={{ color: '#fff', fontSize: '12px' }}>✓</span>}
                  </div>
                </div>

                {itemsAllowed && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '12px', fontWeight: 'bold', color: '#fff' }}>
                      Max Items Per Player
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '8px' }}>
                      {[
                        { value: 0, label: 'Unlimited' },
                        { value: 1, label: '1 item' },
                        { value: 2, label: '2 items' },
                        { value: 3, label: '3 items' },
                        { value: 5, label: '5 items' }
                      ].map(option => (
                        <div key={option.value}
                          onClick={() => setMaxItemsPerPlayer(option.value)}
                          style={{
                            padding: '12px 8px',
                            border: `2px solid ${maxItemsPerPlayer === option.value ? themeColors.primary : 'rgba(255, 255, 255, 0.1)'}`,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            background: maxItemsPerPlayer === option.value ? `${themeColors.primary}20` : 'rgba(255, 255, 255, 0.05)',
                            textAlign: 'center',
                            fontSize: '13px',
                            color: '#fff'
                          }}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px',
          borderTop: `1px solid ${themeColors.primary}40`,
          background: 'rgba(0, 0, 0, 0.2)',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '12px 24px',
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#fff',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.1)';
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              padding: '12px 24px',
              background: loading ? 'rgba(107, 114, 128, 0.5)' : themeColors.primary,
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColors.primary}40`;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }
            }}
          >
            <img 
              src="/items/battleseek.png" 
              alt="Challenge" 
              style={{ width: '16px', height: '16px', imageRendering: 'pixelated' }}
            />
            {loading ? 'Sending Challenge...' : 'Send Challenge'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PvPChallengeModal;
